import { useState, useEffect, useCallback } from "react";
import SundayEditionAdmin from "./SundayEditionAdmin";
import HypothesisAdmin from "./HypothesisAdmin";

const API_URL = process.env.REACT_APP_API_URL || "https://api.bearing-intelligence.com";

const THREAT_COLORS = {
  CRITICAL: { border: "#cc2222", text: "#ff4444", dot: "#ff4444", bg: "rgba(204,34,34,0.08)" },
  HIGH:     { border: "#cc6622", text: "#ff8833", dot: "#ff8833", bg: "rgba(204,102,34,0.08)" },
  ELEVATED: { border: "#c4a563", text: "#c4a563", dot: "#c4a563", bg: "rgba(196,165,99,0.08)" },
  MODERATE: { border: "#4a5f82", text: "#8a9bbf", dot: "#8a9bbf", bg: "rgba(74,95,130,0.08)" },
  LOW:      { border: "#2a6640", text: "#44aa66", dot: "#44aa66", bg: "rgba(42,102,64,0.08)" },
};

const STATUS_LABELS = {
  pending_approval: { label: "Pending Review", color: "#c4a563" },
  approved:         { label: "Approved",        color: "#44aa66" },
  published:        { label: "Published",        color: "#4a5f82" },
  rejected:         { label: "Rejected",         color: "#cc4444" },
};

function ThreatBadge({ level }) {
  const c = THREAT_COLORS[level] || THREAT_COLORS.MODERATE;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "3px 10px", borderRadius: 3,
      background: c.bg, border: `1px solid ${c.border}`,
      color: c.text, fontSize: 11,
      fontFamily: "'Crimson Text', Georgia, serif",
      fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: c.dot, flexShrink: 0 }} />
      {level}
    </span>
  );
}

function StatusBadge({ status }) {
  const s = STATUS_LABELS[status] || { label: status, color: "#888" };
  return (
    <span style={{
      padding: "2px 8px", borderRadius: 3,
      border: `1px solid ${s.color}44`,
      color: s.color, fontSize: 11,
      fontFamily: "'Crimson Text', Georgia, serif",
      letterSpacing: "0.04em",
    }}>
      {s.label}
    </span>
  );
}

function DraftCard({ draft, onApprove, onReject, onView, onPublishNow, isExpanded }) {
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const [loading, setLoading] = useState(false);

  const threatLevel = typeof draft.threat_level === "string"
    ? draft.threat_level.replace(/"/g, "")
    : draft.threat_level;

  const summary = typeof draft.executive_summary === "string"
    ? draft.executive_summary.replace(/^"|"$/g, "")
    : draft.executive_summary;

  const publishTime = draft.scheduled_publish_at
    ? new Date(draft.scheduled_publish_at).toLocaleTimeString("en-US", {
        timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", timeZoneName: "short"
      })
    : "0800 ET";

  return (
    <div style={{
      background: "rgba(26, 31, 46, 0.6)",
      border: "1px solid rgba(196, 165, 99, 0.3)",
      borderRadius: 8, overflow: "hidden",
      transition: "border-color 0.2s ease",
    }}
      onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(196,165,99,0.6)"}
      onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(196,165,99,0.3)"}
    >
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        padding: "12px 12px", borderBottom: "1px solid rgba(196,165,99,0.15)",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
          <span style={{
            fontFamily: "'Garamond', Georgia, serif",
            fontSize: "clamp(11px, 3vw, 13px)", color: "#8a8b8f", letterSpacing: "0.02em",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
          }}>
            {new Date(draft.date).toLocaleDateString("en-US", {
              weekday: "short", month: "short", day: "numeric", timeZone: "UTC",
            })}
          </span>
          <ThreatBadge level={threatLevel} />
          <StatusBadge status={draft.status} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", flex: "0 0 auto" }}>
          <span style={{ fontSize: 10, color: "#4a5f82", fontFamily: "'Crimson Text', Georgia, serif", whiteSpace: "nowrap" }}>
            {draft.development_count}
          </span>
          <button onClick={() => onView(draft.id)} style={{
            background: "transparent", border: "1px solid rgba(196,165,99,0.4)",
            color: "#c4a563", padding: "4px 10px", borderRadius: 4,
            cursor: "pointer", fontSize: "clamp(10px, 2.5vw, 12px)",
            fontFamily: "'Garamond', Georgia, serif",
            transition: "all 0.2s", whiteSpace: "nowrap", minWidth: "40px", textAlign: "center"
          }}
            onMouseEnter={e => { e.target.style.background = "rgba(196,165,99,0.1)"; }}
            onMouseLeave={e => { e.target.style.background = "transparent"; }}
          >
            {isExpanded ? "↓" : "→"}
          </button>
        </div>
      </div>

      {/* Summary */}
      <div style={{ padding: "10px 12px", borderBottom: "1px solid rgba(196,165,99,0.1)" }}>
        <p style={{
          margin: 0, fontSize: "clamp(13px, 3vw, 15px)", color: "#8a8b8f", lineHeight: 1.6,
          fontFamily: "'Crimson Text', Georgia, serif", fontStyle: "italic",
        }}>
          {summary}
        </p>
      </div>

      {/* Expanded full briefing */}
      {isExpanded && draft.fullData && (
        <FullBriefingPreview data={draft.fullData} />
      )}

      {/* Actions */}
      {draft.status === "pending_approval" && (
        <div style={{
          padding: "10px 12px",
          background: "rgba(15,20,25,0.4)",
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          borderTop: "1px solid rgba(196,165,99,0.1)",
          justifyContent: "flex-start",
        }}>
          <button
            onClick={async () => { setLoading(true); await onApprove(draft); setLoading(false); }}
            disabled={loading}
            style={{
              background: "rgba(42,102,64,0.2)", border: "1px solid #2a6640",
              color: "#44aa66", padding: "6px 14px", borderRadius: 4,
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "clamp(11px, 2.5vw, 12px)", fontFamily: "'Garamond', Georgia, serif",
              fontWeight: 700, opacity: loading ? 0.6 : 1,
              transition: "all 0.2s", whiteSpace: "nowrap",
            }}
            onMouseEnter={e => { if (!loading) e.target.style.background = "rgba(42,102,64,0.35)"; }}
            onMouseLeave={e => { e.target.style.background = "rgba(42,102,64,0.2)"; }}
          >
            ✓ Approve
          </button>

          {!showRejectInput ? (
            <button onClick={() => setShowRejectInput(true)} style={{
              background: "transparent", border: "1px solid rgba(204,68,68,0.4)",
              color: "#cc4444", padding: "6px 14px", borderRadius: 4,
              cursor: "pointer", fontSize: "clamp(11px, 2.5vw, 12px)",
              fontFamily: "'Garamond', Georgia, serif",
              transition: "all 0.2s", whiteSpace: "nowrap",
            }}
              onMouseEnter={e => { e.target.style.background = "rgba(204,68,68,0.1)"; }}
              onMouseLeave={e => { e.target.style.background = "transparent"; }}
            >
              ✕ Reject
            </button>
          ) : (
            <div style={{ display: "flex", gap: 6, flex: 1, minWidth: "200px", flexWrap: "wrap" }}>
              <input
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                placeholder="Reason..."
                style={{
                  flex: 1, minWidth: "120px", background: "rgba(15,20,25,0.6)",
                  border: "1px solid rgba(196,165,99,0.3)",
                  color: "#e8e9ed", padding: "6px 10px", borderRadius: 4,
                  fontSize: "clamp(11px, 2.5vw, 13px)", fontFamily: "'Crimson Text', Georgia, serif",
                  outline: "none",
                }}
              />
              <button
                onClick={async () => {
                  if (!rejectReason.trim()) return;
                  setLoading(true);
                  await onReject(draft, rejectReason);
                  setLoading(false);
                  setShowRejectInput(false);
                  setRejectReason("");
                }}
                disabled={!rejectReason.trim() || loading}
                style={{
                  background: "rgba(204,68,68,0.2)", border: "1px solid #cc4444",
                  color: "#ff6666", padding: "6px 14px", borderRadius: 4,
                  cursor: "pointer", fontSize: 13,
                  fontFamily: "'Garamond', Georgia, serif",
                  opacity: (!rejectReason.trim() || loading) ? 0.5 : 1,
                }}>
                Confirm
              </button>
              <button onClick={() => { setShowRejectInput(false); setRejectReason(""); }} style={{
                background: "transparent", border: "1px solid rgba(196,165,99,0.2)",
                color: "#8a8b8f", padding: "6px 10px", borderRadius: 4,
                cursor: "pointer", fontSize: 13,
                fontFamily: "'Garamond', Georgia, serif",
              }}>
                Cancel
              </button>
            </div>
          )}
        </div>
      )}

      {draft.status === "approved" && (
        <div style={{ padding: "10px 12px", borderTop: "1px solid rgba(196,165,99,0.1)", display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", background: "rgba(15,20,25,0.4)", justifyContent: "space-between" }}>
          <span style={{ fontSize: "clamp(11px, 2.5vw, 12px)", color: "#44aa66", fontFamily: "'Crimson Text', Georgia, serif", fontStyle: "italic", whiteSpace: "nowrap" }}>
            ⏰ {publishTime} ET
          </span>
          <button
            onClick={async () => { setLoading(true); await onPublishNow(draft); setLoading(false); }}
            disabled={loading}
            style={{
              background: "rgba(196,165,99,0.2)", border: "1px solid #c4a563",
              color: "#c4a563", padding: "6px 14px", borderRadius: 4,
              cursor: loading ? "not-allowed" : "pointer",
              fontSize: "clamp(11px, 2.5vw, 12px)", fontFamily: "'Garamond', Georgia, serif",
              fontWeight: 700, opacity: loading ? 0.6 : 1,
              transition: "all 0.2s", whiteSpace: "nowrap",
            }}
            onMouseEnter={e => { if (!loading) e.target.style.background = "rgba(196,165,99,0.3)"; }}
            onMouseLeave={e => { e.target.style.background = "rgba(196,165,99,0.2)"; }}
          >
            {loading ? "Publishing..." : "📤 Publish Now"}
          </button>
        </div>
      )}

      {draft.status === "published" && draft.published_at && (
        <div style={{ padding: "10px 20px", borderTop: "1px solid rgba(196,165,99,0.1)" }}>
          <span style={{ fontSize: 12, color: "#4a5f82", fontFamily: "'Crimson Text', Georgia, serif", fontStyle: "italic" }}>
            Published {new Date(draft.published_at).toLocaleString("en-US", { timeZone: "America/New_York" })} ET
          </span>
        </div>
      )}

      {draft.status === "rejected" && (
        <div style={{ padding: "10px 20px", borderTop: "1px solid rgba(196,165,99,0.1)" }}>
          <span style={{ fontSize: 12, color: "#cc4444", fontFamily: "'Crimson Text', Georgia, serif", fontStyle: "italic" }}>
            Rejected: {draft.rejection_reason}
          </span>
        </div>
      )}
    </div>
  );
}

function FullBriefingPreview({ data }) {
  return (
    <div style={{
      padding: "20px", borderBottom: "1px solid rgba(196,165,99,0.15)",
      background: "rgba(15,20,25,0.3)",
    }}>
      {/* Key Developments */}
      {data.key_developments && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{
            fontFamily: "'Garamond', Georgia, serif",
            fontSize: 13, color: "#c4a563", letterSpacing: "0.1em",
            textTransform: "uppercase", marginBottom: 12,
            paddingBottom: 8, borderBottom: "1px solid rgba(196,165,99,0.15)",
          }}>
            Key Developments
          </h3>
          {data.key_developments.map((dev, i) => {
            const c = THREAT_COLORS[dev.severity] || THREAT_COLORS.MODERATE;
            return (
              <div key={i} style={{
                marginBottom: 14, paddingLeft: 14,
                borderLeft: `3px solid ${c.border}`,
              }}>
                <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 4 }}>
                  <span style={{
                    fontFamily: "'Garamond', Georgia, serif",
                    fontSize: 15, color: "#e8e9ed", fontWeight: 700,
                  }}>
                    {dev.headline}
                  </span>
                  <span style={{ fontSize: 11, color: "#4a5f82", fontFamily: "'Crimson Text', Georgia, serif" }}>
                    {dev.region} · {dev.domain}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 14, color: "#8a8b8f", lineHeight: 1.7, fontFamily: "'Crimson Text', Georgia, serif" }}>
                  {dev.assessment}
                </p>
                {dev.implications && (
                  <p style={{ margin: "4px 0 0", fontSize: 13, color: "#4a5f82", lineHeight: 1.6, fontStyle: "italic", fontFamily: "'Crimson Text', Georgia, serif" }}>
                    {dev.implications}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Strategic Pattern */}
      {data.strategic_pattern && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{
            fontFamily: "'Garamond', Georgia, serif",
            fontSize: 13, color: "#c4a563", letterSpacing: "0.1em",
            textTransform: "uppercase", marginBottom: 10,
            paddingBottom: 8, borderBottom: "1px solid rgba(196,165,99,0.15)",
          }}>
            Strategic Pattern
          </h3>
          <p style={{ margin: 0, fontSize: 14, color: "#e8e9ed", lineHeight: 1.7, fontFamily: "'Crimson Text', Georgia, serif" }}>
            {data.strategic_pattern.observation}
          </p>
          {data.strategic_pattern.significance && (
            <p style={{ margin: "6px 0 0", fontSize: 13, color: "#8a8b8f", lineHeight: 1.6, fontStyle: "italic", fontFamily: "'Crimson Text', Georgia, serif" }}>
              {data.strategic_pattern.significance}
            </p>
          )}
        </div>
      )}

      {/* Sector Watch */}
      {data.sector_watch && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{
            fontFamily: "'Garamond', Georgia, serif",
            fontSize: 13, color: "#c4a563", letterSpacing: "0.1em",
            textTransform: "uppercase", marginBottom: 10,
            paddingBottom: 8, borderBottom: "1px solid rgba(196,165,99,0.15)",
          }}>
            Sector Watch
          </h3>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 12 }}>
            {Object.entries(data.sector_watch).map(([sector, text]) => (
              <div key={sector} style={{
                background: "rgba(196,165,99,0.06)",
                border: "1px solid rgba(196,165,99,0.2)",
                borderRadius: 6, padding: "10px 14px",
              }}>
                <div style={{
                  fontFamily: "'Garamond', Georgia, serif",
                  fontSize: 11, color: "#c4a563",
                  textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 4,
                }}>
                  {sector}
                </div>
                <p style={{ margin: 0, fontSize: 13, color: "#8a8b8f", lineHeight: 1.6, fontFamily: "'Crimson Text', Georgia, serif" }}>
                  {text}
                </p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Monitoring Priorities */}
      {data.monitoring_priorities && (
        <div>
          <h3 style={{
            fontFamily: "'Garamond', Georgia, serif",
            fontSize: 13, color: "#c4a563", letterSpacing: "0.1em",
            textTransform: "uppercase", marginBottom: 10,
            paddingBottom: 8, borderBottom: "1px solid rgba(196,165,99,0.15)",
          }}>
            Monitor Next 72 Hours
          </h3>
          {data.monitoring_priorities.map((item, i) => (
            <div key={i} style={{ display: "flex", gap: 12, marginBottom: 8, alignItems: "flex-start" }}>
              <span style={{
                fontFamily: "'Garamond', Georgia, serif",
                fontSize: 12, color: "#4a5f82", marginTop: 2, flexShrink: 0,
              }}>
                {String(i + 1).padStart(2, "0")}
              </span>
              <span style={{ fontSize: 14, color: "#8a8b8f", lineHeight: 1.6, fontFamily: "'Crimson Text', Georgia, serif" }}>
                {item}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [expandedId, setExpandedId] = useState(null);
  const [toast, setToast] = useState(null);
  const [stats, setStats] = useState({ pending: 0, approved: 0, published: 0, rejected: 0 });
  const [mainTab, setMainTab] = useState("content");
  const [contentTab, setContentTab] = useState("daily");

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchDrafts = useCallback(async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await fetch(`${API_URL}/api/news-derivatives/all${params}`);
      const data = await res.json();
      setDrafts(data.drafts || []);

      const all = await fetch(`${API_URL}/api/news-derivatives/all`);
      const allData = await all.json();
      const counts = { pending: 0, approved: 0, published: 0, rejected: 0 };
      (allData.drafts || []).forEach(d => {
        if (d.status === "pending_approval") counts.pending++;
        else if (d.status === "approved") counts.approved++;
        else if (d.status === "published") counts.published++;
        else if (d.status === "rejected") counts.rejected++;
      });
      setStats(counts);
    } catch (err) {
      showToast("Failed to load drafts", "error");
    }
    setLoading(false);
  }, [statusFilter]);

  useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API_URL}/api/news-derivatives/generate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requested_by: "admin" }),
      });
      const data = await res.json();
      if (data.success) {
        showToast(`Briefing generated — Threat level: ${data.preview.threat_level}`);
        fetchDrafts();
      } else {
        showToast(data.error || "Generation failed", "error");
      }
    } catch (err) {
      showToast("Generation request failed", "error");
    }
    setGenerating(false);
  };

  const handleView = async (id) => {
    if (expandedId === id) { setExpandedId(null); return; }
    const res = await fetch(`${API_URL}/api/news-derivatives/draft/${id}`);
    const data = await res.json();
    if (data.draft) {
      setDrafts(prev => prev.map(d =>
        d.id === id ? { ...d, fullData: data.draft.brief_data, approval_token: data.draft.approval_token } : d
      ));
      setExpandedId(id);
    }
  };

  const handleApprove = async (draft) => {
    if (!draft.approval_token) {
      const res = await fetch(`${API_URL}/api/news-derivatives/draft/${draft.id}`);
      const data = await res.json();
      draft = { ...draft, approval_token: data.draft.approval_token };
    }
    const res = await fetch(`${API_URL}/api/news-derivatives/approve/${draft.approval_token}`, { method: "POST" });
    const data = await res.json();
    if (data.success) {
      showToast(`Approved — publishes at 0800 ET`);
      fetchDrafts();
    } else {
      showToast(data.error || "Approval failed", "error");
    }
  };

  const handleReject = async (draft, reason) => {
    if (!draft.approval_token) {
      const res = await fetch(`${API_URL}/api/news-derivatives/draft/${draft.id}`);
      const data = await res.json();
      draft = { ...draft, approval_token: data.draft.approval_token };
    }
    const res = await fetch(`${API_URL}/api/news-derivatives/reject/${draft.approval_token}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ reason }),
    });
    const data = await res.json();
    if (data.success) {
      showToast("Draft rejected", "error");
      fetchDrafts();
    } else {
      showToast(data.error || "Rejection failed", "error");
    }
  };

  const handlePublishNow = async (draft) => {
    try {
      if (!draft.approval_token) {
        const res = await fetch(`${API_URL}/api/news-derivatives/draft/${draft.id}`);
        const data = await res.json();
        draft = { ...draft, approval_token: data.draft.approval_token };
      }
      
      // First check if already approved
      if (draft.status !== 'approved') {
        showToast("Can only publish approved briefs", "error");
        return;
      }

      // Call publish endpoint (if it exists on backend)
      const res = await fetch(`${API_URL}/api/news-derivatives/publish-now/${draft.approval_token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      
      if (data.success) {
        showToast("📤 Briefing published immediately", "success");
        fetchDrafts();
      } else {
        showToast(data.error || "Failed to publish now", "error");
      }
    } catch (err) {
      showToast("Error publishing: " + err.message, "error");
    }
  };

  const filters = ["all", "pending_approval", "approved", "published", "rejected"];
  const filterLabels = {
    all: "All",
    pending_approval: `Pending${stats.pending ? ` (${stats.pending})` : ""}`,
    approved: "Approved", published: "Published", rejected: "Rejected",
  };

  const filteredDrafts = statusFilter === "all" ? drafts : drafts.filter(d => d.status === statusFilter);

  return (
    <div style={{
      minHeight: "100vh",
      background: "linear-gradient(135deg, #0f1419 0%, #1a1f2e 100%)",
      color: "#e8e9ed",
      fontFamily: "'Crimson Text', Georgia, serif",
    }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 56, right: 20, zIndex: 1000,
          background: toast.type === "error" ? "rgba(26,5,5,0.95)" : "rgba(5,26,14,0.95)",
          border: `1px solid ${toast.type === "error" ? "#cc2222" : "#2a6640"}`,
          color: toast.type === "error" ? "#ff6666" : "#44aa66",
          padding: "10px 18px", borderRadius: 6,
          fontSize: 14, fontFamily: "'Crimson Text', Georgia, serif",
          boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
        }}>
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div style={{
        borderBottom: "3px solid #c4a563",
        padding: "12px 16px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        background: "rgba(15,20,25,0.95)",
        flexWrap: "wrap", gap: 12,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0, flex: 1 }}>
          <span style={{
            fontFamily: "'Garamond', Georgia, serif",
            fontSize: "clamp(18px, 5vw, 22px)", color: "#c4a563", fontWeight: 700, letterSpacing: "-0.5px",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"
          }}>
            Bearing
          </span>
          <span style={{ color: "rgba(196,165,99,0.3)", display: "none" }}>|</span>
          <span style={{
            fontFamily: "'Garamond', Georgia, serif",
            fontSize: "clamp(12px, 3vw, 14px)", color: "#8a8b8f", fontStyle: "italic",
            display: "none",
          }}>
            Editorial Control
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "clamp(8px, 2vw, 16px)", flexWrap: "wrap", justifyContent: "flex-end" }}>
          <div style={{ display: "flex", gap: "clamp(10px, 2vw, 16px)" }}>
            {[
              { label: "Pending", value: stats.pending, color: "#c4a563" },
              { label: "Approved", value: stats.approved, color: "#44aa66" },
              { label: "Published", value: stats.published, color: "#4a5f82" },
            ].map(s => (
              <div key={s.label} style={{ textAlign: "center" }}>
                <div style={{
                  fontSize: "clamp(16px, 4vw, 20px)", fontWeight: 700, color: s.color,
                  fontFamily: "'Garamond', Georgia, serif",
                }}>
                  {s.value}
                </div>
                <div style={{
                  fontSize: "clamp(8px, 2vw, 10px)", color: "#8a8b8f", textTransform: "uppercase",
                  letterSpacing: "0.05em", fontFamily: "'Crimson Text', Georgia, serif",
                }}>
                  {s.label.substring(0, 3)}
                </div>
              </div>
            ))}
          </div>
          <button
            onClick={handleGenerate}
            disabled={generating}
            style={{
              background: generating ? "transparent" : "rgba(196,165,99,0.15)",
              border: `1px solid ${generating ? "rgba(196,165,99,0.2)" : "#c4a563"}`,
              color: generating ? "#4a5f82" : "#c4a563",
              padding: "6px 12px", borderRadius: 4,
              cursor: generating ? "not-allowed" : "pointer",
              fontSize: "clamp(11px, 2.5vw, 12px)", fontFamily: "'Garamond', Georgia, serif",
              fontWeight: 700, letterSpacing: "0.02em",
              transition: "all 0.2s", whiteSpace: "nowrap",
            }}
            onMouseEnter={e => { if (!generating) e.target.style.background = "rgba(196,165,99,0.25)"; }}
            onMouseLeave={e => { e.target.style.background = generating ? "transparent" : "rgba(196,165,99,0.15)"; }}
          >
            {generating ? "⟳" : "+"}
          </button>
        </div>
      </div>


      {/* Main tab switcher */}
      <div style={{ display:"flex", gap:0, borderBottom:"1px solid rgba(196,165,99,0.15)", background:"rgba(15,20,25,0.95)" }}>
        {[["content","Content"],["analytics","Analytics"],["users","Users"]].map(([key,label]) => (
          <button key={key} onClick={() => setMainTab(key)} style={{
            padding:"10px 20px", background:"transparent", border:"none",
            borderBottom: mainTab===key ? "2px solid #c4a563" : "2px solid transparent",
            color: mainTab===key ? "#c4a563" : "#666",
            cursor:"pointer", fontSize:13, fontWeight:"bold",
            fontFamily:"'Crimson Text', Georgia, serif", letterSpacing:"0.3px",
            transition:"all 0.15s"
          }}>{label}</button>
        ))}
      </div>

      {/* Content tab subtabs */}
      {mainTab === "content" && (
        <div style={{ display:"flex", gap:0, borderBottom:"1px solid rgba(196,165,99,0.1)", background:"rgba(26,31,46,0.4)", paddingLeft:"20px" }}>
          {[["daily","Daily Briefings"],["sunday","Sunday Editions"],["audio","Audio Preview"]].map(([key,label]) => (
            <button key={key} onClick={() => setContentTab(key)} style={{
              padding:"8px 16px", background:"transparent", border:"none",
              borderBottom: contentTab===key ? "2px solid #c4a563" : "2px solid transparent",
              color: contentTab===key ? "#c4a563" : "#888",
              cursor:"pointer", fontSize:12, fontWeight:"500",
              fontFamily:"'Crimson Text', Georgia, serif", letterSpacing:"0.2px",
              transition:"all 0.15s"
            }}>{label}</button>
          ))}
        </div>
      )}

      {/* CONTENT TAB */}
      {mainTab === "content" && (
        <div>
          {contentTab === "daily" && (
            <div>
              {/* Filters */}
              <div style={{
                padding: "8px 12px", borderBottom: "1px solid rgba(196,165,99,0.15)",
                display: "flex", gap: 4, alignItems: "center",
                background: "rgba(26,31,46,0.6)",
                flexWrap: "wrap", overflowX: "auto",
              }}>
                {filters.map(f => (
                  <button key={f} onClick={() => setStatusFilter(f)} style={{
                    background: statusFilter === f ? "rgba(196,165,99,0.15)" : "transparent",
                    border: `1px solid ${statusFilter === f ? "rgba(196,165,99,0.4)" : "transparent"}`,
                    color: statusFilter === f ? "#c4a563" : "#8a8b8f",
                    padding: "5px 12px", borderRadius: 4,
                    cursor: "pointer", fontSize: "clamp(11px, 2.5vw, 12px)",
                    fontFamily: "'Crimson Text', Georgia, serif",
                    transition: "all 0.15s", whiteSpace: "nowrap",
                  }}>
                    {filterLabels[f]}
                  </button>
                ))}
                <button onClick={fetchDrafts} style={{
                  marginLeft: "auto", background: "transparent",
                  border: "1px solid rgba(196,165,99,0.2)",
                  color: "#8a8b8f", padding: "5px 10px", borderRadius: 4,
                  cursor: "pointer", fontSize: "clamp(10px, 2.5vw, 12px)",
                  fontFamily: "'Crimson Text', Georgia, serif", whiteSpace: "nowrap",
                }}>↺</button>
              </div>

              {/* Content */}
              <div style={{ padding: "20px 12px", maxWidth: 960, margin: "0 auto" }}>
                {loading ? (
                  <div style={{ color: "#8a8b8f", fontFamily: "'Crimson Text', Georgia, serif", fontSize: "clamp(13px, 3vw, 15px)", padding: "40px 0", fontStyle: "italic" }}>
                    Loading briefings...
                  </div>
                ) : drafts.length === 0 ? (
                  <div style={{
                    color: "#8a8b8f", fontFamily: "'Crimson Text', Georgia, serif",
                    fontSize: "clamp(13px, 3vw, 15px)", padding: "40px 0",
                    fontStyle: "italic", textAlign: "center",
                  }}>
                    No briefings found.
                  </div>
                ) : (
                  <div>
                    {filteredDrafts.map(draft => (
                      <DraftCard
                        key={draft.id}
                        draft={draft}
                        onApprove={handleApprove}
                        onReject={handleReject}
                        onView={handleView}
                        onPublishNow={handlePublishNow}
                        isExpanded={expandedId === draft.id}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {contentTab === "sunday" && (
            <div style={{ padding:"20px 12px", maxWidth:960, margin:"0 auto" }}>
              <SundayEditionAdmin token={localStorage.getItem("token")} />
            </div>
          )}

          {contentTab === "audio" && (
            <div style={{ padding:"20px 12px", maxWidth:960, margin:"0 auto" }}>
              <div style={{ color:"#8a8b8f", fontSize:14, padding:"40px 20px", textAlign:"center", fontStyle:"italic" }}>
                Audio preview library — playback and archive of all briefing audio
              </div>
            </div>
          )}
        </div>
      )}

      {/* ANALYTICS TAB */}
      {mainTab === "analytics" && (
        <div>
          {/* Analytics subtabs */}
          <div style={{ display:"flex", gap:0, borderBottom:"1px solid rgba(196,165,99,0.1)", background:"rgba(26,31,46,0.4)", paddingLeft:"20px" }}>
            {[["dashboard","Dashboard"],["hypotheses","Hypotheses"],["pe","PE Assessments"]].map(([key,label]) => (
              <button key={key} onClick={() => setContentTab(key)} style={{
                padding:"8px 16px", background:"transparent", border:"none",
                borderBottom: contentTab===key ? "2px solid #c4a563" : "2px solid transparent",
                color: contentTab===key ? "#c4a563" : "#888",
                cursor:"pointer", fontSize:12, fontWeight:"500",
                fontFamily:"'Crimson Text', Georgia, serif", letterSpacing:"0.2px",
                transition:"all 0.15s"
              }}>{label}</button>
            ))}
          </div>

          {contentTab === "dashboard" && (
            <div style={{ padding:"20px 12px", maxWidth:960, margin:"0 auto" }}>
              <ProductAnalyticsDashboard />
            </div>
          )}

          {contentTab === "hypotheses" && (
            <div style={{ padding:"20px 12px", maxWidth:960, margin:"0 auto" }}>
              <HypothesisAdmin token={localStorage.getItem("token")} />
            </div>
          )}

          {contentTab === "pe" && (
            <div style={{ padding:"20px 12px", maxWidth:960, margin:"0 auto" }}>
              <div style={{ color:"#8a8b8f", fontSize:14, padding:"40px 20px", textAlign:"center", fontStyle:"italic" }}>
                PE (Probability Estimate) assessment data and trend analysis
              </div>
            </div>
          )}
        </div>
      )}

      {/* USERS TAB */}
      {mainTab === "users" && (
        <BetaAdminTab />
      )}
    </div>
  );
}

// ── Beta Admin Tab ──────────────────────────────────────────────────────────
function BetaAdminTab() {
  const token = localStorage.getItem('token');
  const [tab, setTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [form, setForm] = useState({ name:'', email:'', phone:'', notes:'' });
  const [inviteLink, setInviteLink] = useState(null);
  const [inviteName, setInviteName] = useState('');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inviteSuccess, setInviteSuccess] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({});

  const headers = { 'Content-Type':'application/json', 'Authorization':`Bearer ${token}` };

  const load = async () => {
    const [u, f, up] = await Promise.all([
      fetch(`${API_URL}/api/admin/beta/users`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/api/admin/beta/feedback`, { headers }).then(r => r.json()),
      fetch(`${API_URL}/api/admin/beta/updates`, { headers }).then(r => r.json()),
    ]);
    setUsers(u.users || []);
    setFeedback(f.feedback || []);
    setUpdates(up.updates || []);
  };

  useState(() => { load(); }, []);

  const createInvite = async () => {
    if (!form.name || !form.email) return;
    setLoading(true);
    const res = await fetch(`${API_URL}/api/admin/beta/invite`, {
      method:'POST', headers,
      body: JSON.stringify(form)
    });
    const data = await res.json();
    if (data.invite_link) {
      setInviteLink(data.invite_link);
      setInviteName(form.name);
      setInviteSuccess(true);
    }
    setLoading(false);
    load();
  };

  const resetInviteForm = () => {
    setInviteSuccess(false);
    setInviteLink(null);
    setInviteName('');
    setCopied(false);
    setForm({ name:'', email:'', phone:'', notes:'' });
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const s = {
    panel: { backgroundColor:'#0f1520', border:'1px solid #1e2a3a', borderRadius:6, padding:16, marginBottom:12 },
    label: { fontSize:10, fontWeight:600, letterSpacing:'0.8px', textTransform:'uppercase', color:'#4a5568', marginBottom:4, display:'block' },
    input: { width:'100%', backgroundColor:'#090d14', border:'1px solid #1e2a3a', borderRadius:4, padding:'8px 12px', fontSize:13, color:'#e2e8f0', fontFamily:'Inter,sans-serif', marginBottom:10 },
    btn: { backgroundColor:'#c4a563', color:'#090d14', border:'none', borderRadius:4, padding:'8px 16px', fontSize:12, fontWeight:600, cursor:'pointer' },
    row: { display:'flex', gap:8, marginBottom:8, alignItems:'flex-start', padding:'10px 0', borderBottom:'1px solid #1e2a3a' },
    name: { color:'#e2e8f0', fontSize:13, fontWeight:600, marginBottom:2 },
    meta: { color:'#4a5568', fontSize:11 },
    badge: { fontSize:10, fontWeight:600, padding:'2px 7px', borderRadius:3, letterSpacing:'0.4px' },
  };

  const tabStyle = (t) => ({
    fontSize:12, fontWeight:500, color: tab===t ? '#c4a563' : '#555',
    background:'none', border:'none', borderBottom: tab===t ? '2px solid #c4a563' : '2px solid transparent',
    padding:'8px 14px', cursor:'pointer', fontFamily:'inherit'
  });

  return (
    <div>
      {/* Tab nav */}
      <div style={{ display:'flex', borderBottom:'1px solid #1e2a3a', marginBottom:16 }}>
        {['users','feedback','updates','invite'].map(t => (
          <button key={t} style={tabStyle(t)} onClick={() => setTab(t)}>
            {t === 'users' ? `Users (${users.length})` :
             t === 'invite' ? 'Send Invite' :
             t === 'feedback' ? `Feedback (${feedback.length})` :
             `Suggestions (${updates.length})`}
          </button>
        ))}
      </div>

      {/* INVITE */}
      {tab === 'invite' && (
        <div style={s.panel}>
          {inviteSuccess ? (
            <div>
              <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
                <div style={{ width:32, height:32, borderRadius:'50%', backgroundColor:'rgba(82,183,136,0.15)', border:'1px solid rgba(82,183,136,0.3)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:16, flexShrink:0 }}>✓</div>
                <div>
                  <div style={{ color:'#52b788', fontSize:13, fontWeight:600 }}>Invitation created for {inviteName}</div>
                  <div style={{ color:'#4a5568', fontSize:11, marginTop:2 }}>Link is unique and single-use. Share it directly with the invitee.</div>
                </div>
              </div>
              <div style={{ backgroundColor:'#090d14', border:'1px solid #1e2a3a', borderRadius:4, padding:'10px 12px', marginBottom:12 }}>
                <div style={{ color:'#4a5568', fontSize:10, fontWeight:600, letterSpacing:'0.8px', textTransform:'uppercase', marginBottom:6 }}>INVITE LINK</div>
                <div style={{ color:'#8896a8', fontSize:12, wordBreak:'break-all', lineHeight:1.5 }}>{inviteLink}</div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <button style={s.btn} onClick={copyLink}>
                  {copied ? '✓ Copied!' : 'Copy Link'}
                </button>
                <button onClick={resetInviteForm} style={{ backgroundColor:'transparent', border:'1px solid #1e2a3a', borderRadius:4, padding:'8px 16px', fontSize:12, fontWeight:600, color:'#8896a8', cursor:'pointer', fontFamily:'inherit' }}>
                  + Invite Another
                </button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ color:'#c4a563', fontSize:13, fontWeight:600, marginBottom:16 }}>Create Beta Invitation</div>
              <label style={s.label}>Name *</label>
              <input style={s.input} value={form.name} onChange={e => setForm({...form, name:e.target.value})} placeholder="Full name" />
              <label style={s.label}>Email *</label>
              <input style={s.input} value={form.email} onChange={e => setForm({...form, email:e.target.value})} placeholder="email@domain.com" />
              <label style={s.label}>Phone (for SMS)</label>
              <input style={s.input} value={form.phone} onChange={e => setForm({...form, phone:e.target.value})} placeholder="+1 555 000 0000" />
              <label style={s.label}>Notes</label>
              <input style={s.input} value={form.notes} onChange={e => setForm({...form, notes:e.target.value})} placeholder="Optional context" />
              <button style={s.btn} onClick={createInvite} disabled={loading || !form.name || !form.email}>
                {loading ? 'Generating…' : 'Generate Invite Link'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* USERS */}
      {tab === 'users' && (
        <div style={{ maxWidth:720 }}>
          {users.length === 0 ? (
            <div style={{ color:'#444', fontSize:12, padding:'20px 0' }}>No beta users yet. Create an invitation to get started.</div>
          ) : users.map(u => (
            <div key={u.id} style={{ backgroundColor:'#0f1520', border:'1px solid #1e2a3a', borderRadius:6, padding:'12px 16px', marginBottom:8, display:'flex', alignItems:'flex-start', gap:12 }}>
              {editingUser === u.id ? (
                <div style={{ flex:1, minWidth:0 }}>
                  <input style={{...s.input, marginBottom:6}} value={editForm.name||''} onChange={e=>setEditForm({...editForm,name:e.target.value})} placeholder="Name" />
                  <input style={{...s.input, marginBottom:6}} value={editForm.phone||''} onChange={e=>setEditForm({...editForm,phone:e.target.value})} placeholder="Phone" />
                  <input style={{...s.input, marginBottom:8}} value={editForm.notes||''} onChange={e=>setEditForm({...editForm,notes:e.target.value})} placeholder="Notes" />
                  <div style={{ display:'flex', gap:8 }}>
                    <button style={s.btn} onClick={async () => {
                      await fetch(`${API_URL}/api/admin/beta/users/${u.id}`, { method:'PATCH', headers, body: JSON.stringify(editForm) });
                      setEditingUser(null); load();
                    }}>Save</button>
                    <button style={{...s.btn, backgroundColor:'transparent', color:'#555', border:'1px solid #1e2a3a'}} onClick={() => setEditingUser(null)}>Cancel</button>
                  </div>
                </div>
              ) : (
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:3 }}>
                    <div style={{ color:'#e2e8f0', fontSize:13, fontWeight:600 }}>{u.name}</div>
                    <span style={{ fontSize:10, fontWeight:600, padding:'1px 6px', borderRadius:3, letterSpacing:'0.4px',
                      backgroundColor: u.status==='active' ? 'rgba(45,106,79,0.2)' : 'rgba(74,86,104,0.15)',
                      color: u.status==='active' ? '#52b788' : '#4a5568' }}>
                      {u.status}
                    </span>
                  </div>
                  <div style={{ color:'#4a5568', fontSize:12, marginBottom: u.notes ? 3 : 0 }}>{u.email}{u.phone ? ` · ${u.phone}` : ''}</div>
                  {u.notes && <div style={{ color:'#4a5568', fontSize:11, fontStyle:'italic' }}>{u.notes}</div>}
                  <div style={{ display:'flex', gap:12, marginTop:5, flexWrap:'wrap' }}>
                    {u.login_count > 0 && <span style={{ color:'#3d6494', fontSize:11 }}>{u.login_count} login{u.login_count!==1?'s':''}</span>}
                    {u.last_login_at && <span style={{ color:'#4a5568', fontSize:11 }}>Last seen {new Date(u.last_login_at).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>}
                    {u.feedback_count > 0 && <span style={{ color:'#c4a563', fontSize:11 }}>{u.feedback_count} feedback item{u.feedback_count!==1?'s':''}</span>}
                    {u.invited_at && <span style={{ color:'#4a5568', fontSize:11 }}>Invited {new Date(u.invited_at).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>}
                  </div>
                </div>
              )}
              {editingUser !== u.id && (
                <div style={{ display:'flex', gap:6, flexShrink:0, marginTop:2 }}>
                  <button onClick={() => { setEditingUser(u.id); setEditForm({ name:u.name, phone:u.phone||'', notes:u.notes||'' }); }}
                    style={{ background:'none', border:'1px solid #1e2a3a', borderRadius:4, color:'#8896a8', fontSize:11, padding:'4px 10px', cursor:'pointer', whiteSpace:'nowrap' }}>
                    Edit
                  </button>
                  <button onClick={async () => {
                    if (!window.confirm(`Remove ${u.name} from beta?`)) return;
                    await fetch(`${API_URL}/api/admin/beta/users/${u.id}`, { method:'DELETE', headers });
                    load();
                  }}
                    style={{ background:'none', border:'1px solid rgba(201,64,64,0.25)', borderRadius:4, color:'#c94040', fontSize:11, padding:'4px 10px', cursor:'pointer', whiteSpace:'nowrap' }}>
                    Delete
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* FEEDBACK */}
      {tab === 'feedback' && (
        <div>
          {feedback.length === 0 ? (
            <div style={{ color:'#444', fontSize:12, padding:'20px 0' }}>No feedback submitted yet.</div>
          ) : feedback.map(f => (
            <div key={f.id} style={s.row}>
              <div style={{ flex:1 }}>
                <div style={{ display:'flex', gap:8, marginBottom:4 }}>
                  <span style={{ color:'#c4a563', fontSize:12, fontWeight:600 }}>{f.beta_user_name || 'Anonymous'}</span>
                  <span style={{ color:'#4a5568', fontSize:11 }}>{f.category}</span>
                  <span style={{ color:'#4a5568', fontSize:11 }}>{new Date(f.created_at).toLocaleDateString()}</span>
                </div>
                <div style={{ color:'#8896a8', fontSize:13, lineHeight:1.6, marginBottom:6 }}>{f.feedback_text}</div>
                {f.proposed_updates?.length > 0 && (
                  <div style={{ backgroundColor:'rgba(196,165,99,0.05)', border:'1px solid rgba(196,165,99,0.1)', borderRadius:4, padding:'6px 10px' }}>
                    <div style={{ color:'#c4a563', fontSize:10, fontWeight:600, marginBottom:2 }}>PROPOSED UPDATE</div>
                    <div style={{ color:'#8896a8', fontSize:12 }}>{f.proposed_updates[0].title}</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* UPDATES */}
      {tab === 'updates' && (
        <div>
          {updates.length === 0 ? (
            <div style={{ color:'#444', fontSize:12, padding:'20px 0' }}>No proposed updates yet.</div>
          ) : updates.map(u => (
            <div key={u.id} style={s.row}>
              <div style={{ minWidth:40, textAlign:'center' }}>
                <div style={{ color:'#c4a563', fontSize:16, fontWeight:700, fontFamily:'IBM Plex Mono,monospace' }}>{u.priority_score}</div>
                <div style={{ color:'#4a5568', fontSize:9 }}>PRIORITY</div>
              </div>
              <div style={{ flex:1 }}>
                <div style={s.name}>{u.title}</div>
                {u.description && <div style={{ color:'#8896a8', fontSize:12, lineHeight:1.5, marginTop:2 }}>{u.description}</div>}
                <div style={{ display:'flex', gap:8, marginTop:4 }}>
                  <span style={{...s.badge, backgroundColor:'rgba(74,86,104,0.2)', color:'#8896a8'}}>{u.status}</span>
                  {u.submitter_name && <span style={s.meta}>from {u.submitter_name}</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Product Analytics Dashboard ──────────────────────────────────────────────
function ProductAnalyticsDashboard() {
  const [section, setSection] = useState('usage');
  const [usage, setUsage] = useState(null);
  const [feedback, setFeedback] = useState([]);
  const [updates, setUpdates] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);

  const getHeaders = () => ({
    'Content-Type':'application/json',
    'Authorization':`Bearer ${localStorage.getItem('token')}`
  });

  useEffect(() => {
    setLoading(true);
    Promise.all([
      fetch(`${API_URL}/api/admin/analytics/summary?days=${days}`, { headers: getHeaders() }).then(r => r.json()),
      fetch(`${API_URL}/api/admin/beta/feedback`, { headers: getHeaders() }).then(r => r.json()),
      fetch(`${API_URL}/api/admin/beta/updates`, { headers: getHeaders() }).then(r => r.json()),
    ]).then(([u, f, up]) => {
      setUsage(u);
      setFeedback(f.feedback || []);
      setUpdates(up.updates || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [days]);

  const s = {
    card: { backgroundColor:'#0f1520', border:'1px solid #1e2a3a', borderRadius:6, padding:'14px 16px', marginBottom:8 },
    label: { fontSize:10, fontWeight:600, letterSpacing:'0.8px', textTransform:'uppercase', color:'#4a5568', marginBottom:8, display:'block' },
    big: { fontFamily:'IBM Plex Mono, monospace', fontSize:28, fontWeight:500, color:'#c4a563' },
    sub: { fontSize:11, color:'#4a5568', marginTop:2 },
    row: { display:'flex', justifyContent:'space-between', alignItems:'center', padding:'7px 0', borderBottom:'1px solid rgba(255,255,255,0.03)' },
    sectionBtn: (active) => ({
      fontSize:12, fontWeight:500, background:'none', border:'none',
      borderBottom: active ? '2px solid #c4a563' : '2px solid transparent',
      color: active ? '#c4a563' : '#555', padding:'8px 16px',
      cursor:'pointer', fontFamily:'inherit'
    }),
  };

  const friendlyName = {
    briefing_opened:'Briefing opened', audio_played:'Audio played',
    audio_completed:'Audio completed', map_opened:'Map opened',
    country_panel_opened:'Country viewed', pe_estimate_submitted:'PE estimate submitted',
    dashboard_visited:'Dashboard visited', sources_opened:'Sources opened',
  };

  const categoryColor = {
    general:'#4a5568', content:'#3d6494', design:'#b8956a',
    'feature request':'#52b788', bug:'#c94040'
  };

  const statusColor = { proposed:'#4a5568', 'in progress':'#3d6494', done:'#52b788', declined:'#c94040' };

  if (loading) return <div style={{ color:'#444', fontSize:12, padding:'20px 0' }}>Loading…</div>;

  return (
    <div>
      {/* Section nav */}
      <div style={{ display:'flex', borderBottom:'1px solid #1e2a3a', marginBottom:20 }}>
        {['usage','feedback','suggestions'].map(s2 => (
          <button key={s2} style={s.sectionBtn(section===s2)} onClick={() => setSection(s2)}>
            {s2 === 'usage' ? 'Usage' :
             s2 === 'feedback' ? `Feedback (${feedback.length})` :
             `Suggestions (${updates.length})`}
          </button>
        ))}
      </div>

      {/* USAGE */}
      {section === 'usage' && (
        <div style={{ maxWidth:840 }}>
          <div style={{ display:'flex', gap:8, marginBottom:16, alignItems:'center' }}>
            <span style={{ color:'#4a5568', fontSize:12 }}>Period:</span>
            {[7,14,30].map(d => (
              <button key={d} onClick={() => setDays(d)} style={{
                background: days===d ? 'rgba(196,165,99,0.15)' : 'none',
                border:`1px solid ${days===d ? 'rgba(196,165,99,0.3)' : '#1e2a3a'}`,
                borderRadius:4, padding:'4px 10px', fontSize:11, fontWeight:600,
                color: days===d ? '#c4a563' : '#555', cursor:'pointer', fontFamily:'inherit'
              }}>{d}d</button>
            ))}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:8, marginBottom:12 }}>
            <div style={s.card}>
              <span style={s.label}>Audio Completion</span>
              <div style={s.big}>{usage?.audio?.completion_rate || 0}%</div>
              <div style={s.sub}>{usage?.audio?.plays || 0} plays · {usage?.audio?.completions || 0} completed</div>
            </div>
            <div style={s.card}>
              <span style={s.label}>PE Submissions</span>
              <div style={s.big}>{usage?.pe_estimates?.submissions || 0}</div>
              <div style={s.sub}>{usage?.pe_estimates?.unique_submitters || 0} unique users</div>
            </div>
            <div style={s.card}>
              <span style={s.label}>Total Events</span>
              <div style={s.big}>{usage?.total_events || 0}</div>
              <div style={s.sub}>last {days} days</div>
            </div>
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:8 }}>
            <div style={s.card}>
              <span style={s.label}>Event Breakdown</span>
              {(!usage?.event_breakdown?.length) && <div style={{ color:'#4a5568', fontSize:12 }}>No events yet</div>}
              {usage?.event_breakdown?.map(e => (
                <div key={e.event_type} style={s.row}>
                  <span style={{ color:'#8896a8', fontSize:12 }}>{friendlyName[e.event_type] || e.event_type}</span>
                  <div style={{ display:'flex', gap:12 }}>
                    <span style={{ fontFamily:'IBM Plex Mono,monospace', fontSize:12, color:'#c4a563' }}>{e.count}</span>
                    <span style={{ fontSize:11, color:'#4a5568' }}>{e.unique_devices}u</span>
                  </div>
                </div>
              ))}
            </div>
            <div style={s.card}>
              <span style={s.label}>Top Countries Viewed</span>
              {(!usage?.top_countries?.length) && <div style={{ color:'#4a5568', fontSize:12 }}>No data yet</div>}
              {usage?.top_countries?.map(c => (
                <div key={c.country} style={s.row}>
                  <span style={{ color:'#8896a8', fontSize:12 }}>{c.country}</span>
                  <span style={{ fontFamily:'IBM Plex Mono,monospace', fontSize:12, color:'#c4a563' }}>{c.views}</span>
                </div>
              ))}
            </div>
          </div>

          <div style={s.card}>
            <span style={s.label}>Daily Active Devices</span>
            {(!usage?.daily_active?.length) ? (
              <div style={{ color:'#4a5568', fontSize:12 }}>No data yet</div>
            ) : (
              <div style={{ display:'flex', gap:4, alignItems:'flex-end', height:56 }}>
                {usage.daily_active.map(d => {
                  const max = Math.max(...usage.daily_active.map(x => parseInt(x.devices)));
                  const h = max > 0 ? Math.round((parseInt(d.devices) / max) * 48) : 4;
                  return (
                    <div key={d.day} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:3 }}>
                      <div style={{ width:'100%', height:h, backgroundColor:'rgba(196,165,99,0.35)', borderRadius:2, minHeight:4 }} />
                      <span style={{ fontSize:9, color:'#4a5568' }}>
                        {new Date(d.day).toLocaleDateString('en-US',{month:'numeric',day:'numeric'})}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FEEDBACK */}
      {section === 'feedback' && (
        <div style={{ maxWidth:840 }}>
          {feedback.length === 0 ? (
            <div style={{ color:'#444', fontSize:12, padding:'20px 0' }}>No feedback submitted yet.</div>
          ) : feedback.map(f => (
            <div key={f.id} style={s.card}>
              <div style={{ display:'flex', gap:10, alignItems:'center', marginBottom:8 }}>
                <span style={{ color:'#c4a563', fontSize:13, fontWeight:600 }}>{f.beta_user_name || 'Anonymous'}</span>
                <span style={{
                  fontSize:10, fontWeight:600, padding:'1px 7px', borderRadius:3,
                  backgroundColor:`${categoryColor[f.category] || '#4a5568'}22`,
                  color: categoryColor[f.category] || '#4a5568',
                  border:`1px solid ${categoryColor[f.category] || '#4a5568'}44`
                }}>{f.category}</span>
                <span style={{ color:'#4a5568', fontSize:11, marginLeft:'auto' }}>
                  {new Date(f.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric'})}
                </span>
              </div>
              <p style={{ color:'#8896a8', fontSize:13, lineHeight:1.65, marginBottom: f.proposed_updates?.length ? 10 : 0 }}>
                {f.feedback_text}
              </p>
              {f.proposed_updates?.length > 0 && (
                <div style={{ backgroundColor:'rgba(196,165,99,0.04)', border:'1px solid rgba(196,165,99,0.12)', borderRadius:4, padding:'8px 10px', marginTop:8 }}>
                  <div style={{ fontSize:10, fontWeight:600, letterSpacing:'0.6px', color:'#c4a563', marginBottom:3 }}>AI-GENERATED SUGGESTION</div>
                  <div style={{ color:'#8896a8', fontSize:12 }}>{f.proposed_updates[0].title}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* SUGGESTIONS */}
      {section === 'suggestions' && (
        <div style={{ maxWidth:840 }}>
          {updates.length === 0 ? (
            <div style={{ color:'#444', fontSize:12, padding:'20px 0' }}>No suggestions yet. Suggestions are generated automatically from user feedback.</div>
          ) : updates.map(u => (
            <div key={u.id} style={{ ...s.card, display:'flex', gap:16, alignItems:'flex-start' }}>
              <div style={{ textAlign:'center', flexShrink:0, minWidth:40 }}>
                <div style={{ fontFamily:'IBM Plex Mono,monospace', fontSize:22, fontWeight:500, color:'#c4a563', lineHeight:1 }}>{u.priority_score}</div>
                <div style={{ fontSize:9, fontWeight:600, letterSpacing:'0.5px', color:'#4a5568', marginTop:2 }}>PRIORITY</div>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ color:'#e2e8f0', fontSize:13, fontWeight:600, marginBottom:4 }}>{u.title}</div>
                {u.description && <div style={{ color:'#8896a8', fontSize:12, lineHeight:1.6, marginBottom:8 }}>{u.description}</div>}
                <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                  <select
                    value={u.status}
                    onChange={async (e) => {
                      await fetch(`${API_URL}/api/admin/beta/updates/${u.id}`, {
                        method:'PATCH', headers: getHeaders(),
                        body: JSON.stringify({ status: e.target.value })
                      });
                      setUpdates(prev => prev.map(x => x.id === u.id ? {...x, status: e.target.value} : x));
                    }}
                    style={{ backgroundColor:'#090d14', border:'1px solid #1e2a3a', borderRadius:4, padding:'3px 8px', fontSize:11, color: statusColor[u.status] || '#4a5568', fontFamily:'inherit', cursor:'pointer' }}
                  >
                    {['proposed','in progress','done','declined'].map(st => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                  {u.submitter_name && <span style={{ color:'#4a5568', fontSize:11 }}>from {u.submitter_name}</span>}
                  <span style={{ color:'#4a5568', fontSize:11 }}>{new Date(u.created_at).toLocaleDateString('en-US',{month:'short',day:'numeric'})}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
